"use client";

import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Switch from "@/components/inputs/Switch";
import { ProgressBarData } from "@/components/misc/ProgressBar";
import { Card, Checkbox } from "@material-tailwind/react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  TooltipItem,
} from "chart.js";
import StackedProgressBar from "@/components/misc/StackedProgressBar";
import { Skeleton } from "@mui/material";

import { Doughnut } from "react-chartjs-2";
import { getBusinessDaysPassed, getTotalBusinessDays } from "@/lib/utils";
import { Tooltip as TooltipComponent } from "@material-tailwind/react";
import LeaderboardCard from "@/components/dashboard/leaderboard-card";
import { UserContext } from "@/app/_components/context/user-context";
import { DealershipContext } from "@/app/_components/context/dealership-context";
import { DeliveryWithExtras } from "@/app/types/types";
import MobileDashboard from "@/app/_components/mobile/dashboard/dashboard";
import DeliveryCalendarCard from "@/components/dashboard/delivery-calendar-card";
import { SectionedProgressBar } from "@/components/misc/ProgressBar";

// Register chart.js components once at module scope (was previously called on
// every render inside the component body).
ChartJS.register(ArcElement, Tooltip, Legend, Title);

type SalesBreakdownData = {
  [key: string]: {
    units: number;
    frontGross: number;
    backGross: number;
  };
};

type SalesOverviewOptions = {
  label: string;
  value: string;
};

const DashboardPage = () => {
  const currentMonth = new Date().toLocaleString("default", { month: "long" });
  const businessDaysPassed = getBusinessDaysPassed();

  const [businessDaysRemaining, setBusinessDaysRemaining] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSalesOverviewOption, setSelectedSalesOverviewOption] =
    useState<SalesOverviewOptions>({
      label: "All Sales Breakdown",
      value: "ALL",
    });

  const [gtgEnabled, setGtgEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const [overviewRange, setOverviewRange] = useState<"MTD" | "WTD" | "YTD">(
    "MTD",
  );
  const [salesRange, setSalesRange] = useState<"MTD" | "WTD" | "YTD">("MTD");

  const [totalUnitsSold, setTotalUnitsSold] = useState<number>();
  const [deliveryData, setDeliveryData] = useState<{
    [key: string]: DeliveryWithExtras[];
  }>();

  const [overviewData, setOverviewData] = useState<{
    unitsBySaleType: { [key: string]: number };
    unitsBySaleStatus: { [key: string]: number };
    unitsByPaymentType: { [key: string]: number };
  }>();

  const businessDays = getTotalBusinessDays();

  const [salesPercentage, setSalesPercentage] = useState<number>(0);
  const [usedPercentage, setUsedPercentage] = useState<number>(0);
  const [newPercentage, setNewPercentage] = useState<number>(0);
  const [progressData, setProgessData] = useState<ProgressBarData[]>([]);

  const { user, userGroups, getPrimaryUserGroup, isMobile, mobileTheme } =
    useContext(UserContext);
  const { currentDealership } = useContext(DealershipContext);

  const [salesData, setSalesData] = useState<SalesBreakdownData>();

  const primaryUserGroup = useMemo(() => {
    if (!currentDealership) return;
    return getPrimaryUserGroup(currentDealership.id);
  }, [currentDealership, getPrimaryUserGroup]);

  const userPermissions = useMemo(() => {
    if (!currentDealership || !user) return [];

    let userSpecificPerms: string[] = [];

    if (user.permissions && user.permissions[currentDealership.id]) {
      userSpecificPerms = user.permissions[currentDealership.id];
    }

    if (!primaryUserGroup) return [];

    const merged = [...userSpecificPerms, ...primaryUserGroup.permissions];

    return (
      merged.filter((value, pos) => {
        return merged.indexOf(value) === pos;
      }) || []
    );
  }, [primaryUserGroup, currentDealership, user]);

  const kFormatter = (num: number) => {
    return Math.abs(num) > 999
      ? Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1)) + "K"
      : (Math.sign(num) * Math.abs(num)).toFixed(2);
  };

  const dealStatusColors = useMemo(() => {
    return {
      LOCATE: "#474eef",
      PENDING: "#f4f4f4",
      FACTORY_ORDERED: "#fffc5c",
      LANDED: "#d96fcd",
      BOOKED: "#42f58d",
      FINALIZED: "#00c6c6",
      CANCELLED: "#ff0000",
    };
  }, []);

  const saleTypeColors = useMemo(
    () => ({
      RETAIL: "#00c6c6",
      WHOLESALE: "#fc7c27",
      WHOLESALE_DIRECT: "#ff0000",
      FLEET: "#4877f8",
      CREDIT_SOLUTIONS: "#42f58d",
      DLA: "#9238f3",
    }),
    [],
  );

  const dealTypeColors = useMemo(
    () => ({
      CASH: "#fdae3f",
      LEASE: "#42f58d",
      FINANCE: "#00c6c6",
    }),
    [],
  );

  const dealStatusOptions = useMemo(() => {
    const order = [
      "PENDING",
      "FACTORY_ORDERED",
      "LANDED",
      "BOOKED",
      "FINALIZED",
      "CANCELLED",
    ];

    const totalUnitsByDealStatus =
      (overviewData &&
        overviewData.unitsBySaleStatus &&
        Object.values(overviewData.unitsBySaleStatus).reduce((acc, curr) => {
          return acc + curr;
        }, 0)) ||
      0;

    return (
      overviewData &&
      Object.entries(overviewData.unitsBySaleStatus)
        .sort(([keyA], [keyB]) => {
          return order.indexOf(keyA) - order.indexOf(keyB);
        })
        .map(([key, value]) => {
          const percentage = (value / totalUnitsByDealStatus) * 100;
          const color = dealStatusColors[key];

          return {
            amount: value,
            key,
            percentage,
            color,
          };
        })
    );
  }, [overviewData, dealStatusColors]);

  const saleTypeOptions = useMemo(() => {
    const order = [
      "RETAIL",
      "WHOLESALE",
      "WHOLESALE_DIRECT",
      "FLEET",
      "CREDIT_SOLUTIONS",
      "DLA",
      "OTHER",
    ];

    const totalUnitsBySaleType =
      (overviewData &&
        overviewData.unitsBySaleType &&
        Object.values(overviewData.unitsBySaleType).reduce((acc, curr) => {
          return acc + curr;
        }, 0)) ||
      0;

    const mapped: {
      [key: string]: number;
    } = {};

    overviewData &&
      Object.entries(overviewData.unitsBySaleType).forEach(([key, value]) => {
        if (key.toLowerCase() === "dla_lease") {
          mapped["DLA"] = mapped["DLA"] ? mapped["DLA"] + value : value;
        } else {
          mapped[key] = mapped[key] ? mapped[key] + value : value;
        }
      });

    return Object.entries(mapped)
      .sort(([keyA], [keyB]) => {
        return order.indexOf(keyA) - order.indexOf(keyB);
      })
      .map(([key, value]) => {
        const percentage = (value / totalUnitsBySaleType) * 100;
        const color = saleTypeColors[key];

        return {
          amount: value,
          key,
          percentage,
          color,
        };
      });
  }, [overviewData, saleTypeColors]);

  const unitsByPaymentTypeOptions = useMemo(() => {
    const order = ["CASH", "LEASE", "FINANCE"];

    const totalUnitsByPaymentType =
      (overviewData &&
        overviewData.unitsByPaymentType &&
        Object.values(overviewData.unitsByPaymentType).reduce((acc, curr) => {
          return acc + curr;
        }, 0)) ||
      0;

    const mapped: {
      [key: string]: number;
    } = {};

    overviewData &&
      Object.entries(overviewData.unitsByPaymentType).forEach(
        ([key, value]) => {
          if (key === null || key.trim().length === 0) {
            mapped["finance".toUpperCase()] = mapped["finance".toUpperCase()]
              ? mapped["finance".toUpperCase()] + value
              : value;
          } else {
            mapped[key.toUpperCase()] = mapped[key.toUpperCase()]
              ? mapped[key.toUpperCase()] + value
              : value;
          }
        },
      );

    return Object.entries(mapped)
      .sort(([keyA], [keyB]) => {
        return (
          order.indexOf(keyA.toLocaleUpperCase()) -
          order.indexOf(keyB.toLocaleUpperCase())
        );
      })
      .map(([key, value]) => {
        const percentage = (value / totalUnitsByPaymentType) * 100;
        const color = dealTypeColors[key.toUpperCase()];

        return {
          amount: value,
          key: key.toLowerCase(),
          percentage,
          color,
        };
      });
  }, [overviewData, dealTypeColors]);

  // Dashboard reporting reads via React Query so revisiting the page serves
  // cached data instantly and refetches in the background instead of re-hitting
  // these endpoints on every navigation. Each seed effect mirrors the cached
  // result into the existing state the render already consumes.
  const { data: deliveryQueryData } = useQuery({
    queryKey: ["dashboard-delivery", currentDealership?.id],
    queryFn: async () => {
      const response = await fetch(`/v2/api/reporting/delivery-data`);
      if (!response.ok) {
        console.error(response.statusText);
        return {} as { [key: string]: DeliveryWithExtras[] };
      }
      return (await response.json()) as { [key: string]: DeliveryWithExtras[] };
    },
    enabled: !!currentDealership?.id,
  });
  useEffect(() => {
    if (deliveryQueryData !== undefined) setDeliveryData(deliveryQueryData);
  }, [deliveryQueryData]);

  const { data: overviewQueryData } = useQuery({
    queryKey: [
      "dashboard-overview",
      currentDealership?.id,
      overviewRange,
      gtgEnabled,
    ],
    queryFn: async () => {
      const response = await fetch(
        `/v2/api/reporting/overview?range=${overviewRange}&gtg=${gtgEnabled}`,
      );
      if (!response.ok) {
        console.error(response.statusText);
        return null;
      }
      return response.json();
    },
    enabled: !!currentDealership?.id && !!primaryUserGroup,
  });
  useEffect(() => {
    if (overviewQueryData) setOverviewData(overviewQueryData);
  }, [overviewQueryData]);

  const { data: salesQueryData, isFetching: salesDataFetching } = useQuery({
    queryKey: [
      "dashboard-sales-data",
      currentDealership?.id,
      salesRange,
      gtgEnabled,
      selectedSalesOverviewOption.value,
    ],
    queryFn: async () => {
      const salesTypeOption =
        selectedSalesOverviewOption.value == "RETAIL" ? true : false;
      const response = await fetch(
        `/v2/api/reporting/sales-data?range=${salesRange}&gtg=${gtgEnabled}&retailOnly=${salesTypeOption}`,
      );
      if (!response.ok) {
        console.error(response.statusText);
        return null;
      }
      return (await response.json()) as SalesBreakdownData;
    },
    enabled: !!currentDealership?.id,
  });
  useEffect(() => {
    if (salesQueryData) setSalesData(salesQueryData);
  }, [salesQueryData]);

  useEffect(() => {
    if (!currentDealership) return;
    setBusinessDaysRemaining(businessDays - businessDaysPassed);
  }, [user, currentDealership, businessDays, businessDaysPassed]);

  const { data: totalUnitsQueryData, isFetching: totalUnitsFetching } =
    useQuery({
      queryKey: ["dashboard-total-units", currentDealership?.id, gtgEnabled],
      queryFn: async () => {
        const date = new Date();
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const response = await fetch(
          `/v2/api/dealerships/${currentDealership?.id}/sales/total?gtg=${gtgEnabled}&startDate=${firstDay.toDateString()}&endDate=${lastDay.toDateString()}`,
        );
        if (!response.ok) {
          console.error(response.statusText);
          return null;
        }
        return response.json();
      },
      enabled: !!currentDealership?.id,
    });
  useEffect(() => {
    if (totalUnitsQueryData !== null && totalUnitsQueryData !== undefined)
      setTotalUnitsSold(totalUnitsQueryData);
  }, [totalUnitsQueryData]);

  // Reflect the two loading-bearing reports into the shared `loading` flag.
  useEffect(() => {
    setLoading(salesDataFetching || totalUnitsFetching);
  }, [salesDataFetching, totalUnitsFetching]);

  useEffect(() => {
    if (salesRange !== "MTD") return;
    if (!totalUnitsSold || !currentDealership) return;

    setSalesPercentage(
      (totalUnitsSold /
        currentDealership.dealershipConfiguration.unitsSoldTarget) *
        100,
    );
    if (!salesData) return;

    setProgessData([
      {
        color: "#5278f6",
        progress: salesData["NEW"]
          ? (salesData["NEW"].units * 100) /
            currentDealership.dealershipConfiguration.unitsSoldTarget
          : 0,
        label: salesData["NEW"] ? `${salesData["NEW"].units}` : "",
        labelLong: salesData["NEW"] ? `New ${salesData["NEW"].units}` : "",
      },
      {
        color: "#f4ba4e",
        progress: salesData["USED"]
          ? (salesData["USED"].units * 100) /
            currentDealership.dealershipConfiguration.unitsSoldTarget
          : 0,
        label: salesData["USED"] ? `${salesData["USED"].units}` : "",
        labelLong: salesData["USED"] ? `Used ${salesData["USED"].units}` : "",
      },
    ]);
  }, [currentDealership, totalUnitsSold, salesData]);

  const totalUnitsChart = useMemo(() => {
    if (loading) {
      return <Skeleton variant="circular" width={20} />;
    }
    if (!salesData) return;

    const totalUnits =
      (salesData["NEW"] ? salesData["NEW"].units : 0) +
      (salesData["USED"] ? salesData["USED"].units : 0);
    const data = {
      labels: ["New", "Used"],

      datasets: [
        {
          label: "Units",
          data: [
            salesData["NEW"] ? salesData["NEW"].units : 0,
            salesData["USED"] ? salesData["USED"].units : 0,
          ],
          backgroundColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderWidth: 1,
        },
      ],
    };

    return (
      <Doughnut
        className={
          isMobile
            ? " w-2/6 h-2/6"
            : "lg:w-[200px] lg:h-[200px] md:w-1/3 md:h-1/3"
        }
        data={data}
        redraw={true}
        plugins={[
          {
            id: "custom_canvas_background_color",
            beforeDraw(chart) {
              const { width } = chart;
              const { height } = chart;
              const { ctx } = chart;
              ctx.restore();
              const fontSize = isMobile
                ? (height / 100).toFixed(2)
                : (height / 160).toFixed(2);
              ctx.font = `${fontSize}em sans-serif`;
              ctx.textBaseline = "top";
              ctx.fillStyle = isMobile ? "white" : "black";
              const text = `${totalUnits}`;
              const textX = Math.round(
                (width - ctx.measureText(text).width) / 2,
              );
              const textY = isMobile ? height / 2 - 14 : height / 2;
              ctx.fillText(text, textX, textY);
              ctx.save();
            },
          },
        ]}
        options={{
          cutout: isMobile ? "85%" : "80%",
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: isMobile ? false : true,
              position: "bottom",
              fullSize: false,
              labels: {
                boxWidth: 10,
                boxHeight: 10,
              },
            },
            title: {
              display: isMobile ? false : true,
              position: "top",
              padding: {
                top: 10,
                bottom: 20,
              },
              text: "Total Units Sold",
            },
          },
        }}
      />
    );
  }, [salesData, isMobile, mobileTheme, loading]);

  const totalFrontGrossChart = useMemo(() => {
    if (loading) {
      return <Skeleton variant="circular" width={20} />;
    }

    if (!salesData) return;

    const totalGrossText = kFormatter(
      (salesData["NEW"] ? salesData["NEW"].frontGross : 0) +
        (salesData["USED"] ? salesData["USED"].frontGross : 0),
    );
    const data = {
      labels: ["New", "Used"],

      datasets: [
        {
          label: "Total Gross",
          data: [
            salesData["NEW"] ? salesData["NEW"].frontGross : 0,
            salesData["USED"] ? salesData["USED"].frontGross : 0,
          ],
          backgroundColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderWidth: 1,
        },
      ],
    };

    return (
      <Doughnut
        className={
          isMobile ? " w-2/6 h-2/6" : "md:width-[150px] md:height-[150px]"
        }
        data={data}
        redraw={true}
        plugins={[
          {
            id: "custom_canvas_background_color",
            beforeDraw(chart) {
              const { width } = chart;
              const { height } = chart;
              const { ctx } = chart;
              ctx.restore();
              const fontSize = isMobile
                ? (height / 130).toFixed(2)
                : (height / 160).toFixed(2);
              ctx.font = `${fontSize}em sans-serif`;
              ctx.textBaseline = "top";
              ctx.fillStyle = isMobile ? "white" : "black";
              const text = `$${totalGrossText}`;
              const textX = Math.round(
                (width - ctx.measureText(text).width) / 2,
              );
              const textY = isMobile ? height / 2 - 14 : height / 2;
              ctx.fillText(text, textX, textY);
              ctx.save();
            },
          },
        ]}
        options={{
          cutout: isMobile ? "85%" : "80%",
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (tooltipItem: TooltipItem<"doughnut">) {
                  return `Total Gross: $${kFormatter(
                    tooltipItem.dataset.data[tooltipItem.dataIndex],
                  )}`;
                },
              },
            },
            legend: {
              display: isMobile ? false : true,
              position: "bottom",
              fullSize: false,
              labels: {
                boxWidth: 10,
                boxHeight: 10,
              },
            },
            title: {
              display: isMobile ? false : true,
              position: "top",
              padding: {
                top: 10,
                bottom: 20,
              },
              text: "Total Front Gross",
            },
          },
        }}
      />
    );
    // `loading` gates the skeleton return above, so it must be a dependency —
    // otherwise the memo can get stuck on the skeleton when salesData arrives
    // while loading is still true.
  }, [salesData, isMobile, mobileTheme, loading]);

  const backendGrossChart = useMemo(() => {
    if (!salesData) return;

    const totalGrossText = kFormatter(
      (salesData["NEW"] ? salesData["NEW"].backGross : 0) +
        (salesData["USED"] ? salesData["USED"].backGross : 0),
    );
    const data = {
      labels: ["New", "Used"],

      datasets: [
        {
          label: "Total Gross",
          data: [
            salesData["NEW"] ? salesData["NEW"].backGross : 0,
            salesData["USED"] ? salesData["USED"].backGross : 0,
          ],
          backgroundColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderColor: isMobile
            ? [mobileTheme.NEW, mobileTheme.USED]
            : ["#5278f6", "#f4ba4e"],
          borderWidth: 1,
        },
      ],
    };

    return (
      <Doughnut
        className={
          isMobile ? " w-2/6 h-2/6" : "md:width-[150px] md:height-[150px]"
        }
        data={data}
        redraw={true}
        plugins={[
          {
            id: "custom_canvas_background_color",
            beforeDraw(chart) {
              const { width } = chart;
              const { height } = chart;
              const { ctx } = chart;
              ctx.restore();
              const fontSize = isMobile
                ? (height / 130).toFixed(2)
                : (height / 160).toFixed(2);
              ctx.font = `${fontSize}em sans-serif`;
              ctx.textBaseline = "top";
              ctx.fillStyle = isMobile ? "white" : "black";
              const text = `$${totalGrossText}`;
              const textX = Math.round(
                (width - ctx.measureText(text).width) / 2,
              );
              const textY = isMobile ? height / 2 - 14 : height / 2;
              ctx.fillText(text, textX, textY);
              ctx.save();
            },
          },
        ]}
        options={{
          cutout: isMobile ? "85%" : "80%",
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (tooltipItem: TooltipItem<"doughnut">) {
                  return `Total Gross: $${kFormatter(
                    tooltipItem.dataset.data[tooltipItem.dataIndex],
                  )}`;
                },
              },
            },
            legend: {
              display: isMobile ? false : true,
              position: "bottom",
              fullSize: false,
              labels: {
                boxWidth: 10,
                boxHeight: 10,
              },
            },
            title: {
              display: isMobile ? false : true,
              position: "top",
              padding: {
                top: 10,
                bottom: 20,
              },
              text: "Total Back Gross",
            },
          },
        }}
      />
    );
  }, [salesData, isMobile, mobileTheme]);

  return (
    <>
      {isMobile ? (
        <MobileDashboard
          setLoading={setLoading}
          loading={loading}
          setGtgEnabled={setGtgEnabled}
          gtgEnabled={gtgEnabled}
          salesData={salesData || {}}
          salesChart={totalUnitsChart}
          frontGrossChart={
            userPermissions && userPermissions.includes("VIEW_OWN_GROSS")
              ? totalFrontGrossChart
              : undefined
          }
          backGrossChart={
            userPermissions && userPermissions.includes("VIEW_DASHBOARD_FULL")
              ? backendGrossChart
              : undefined
          }
          selectedSalesOverviewOption={selectedSalesOverviewOption}
          setSelectedSalesOverviewOption={setSelectedSalesOverviewOption}
          overviewRange={overviewRange}
          setOverviewRange={setOverviewRange}
          setSalesRange={setSalesRange}
          dealStatusOptions={dealStatusOptions || []}
          saleTypeOptions={saleTypeOptions}
          unitsByPaymentTypeOptions={unitsByPaymentTypeOptions}
          primaryUserGroup={primaryUserGroup && primaryUserGroup.name}
          deliveries={deliveryData || {}}
          userPermissions={userPermissions || []}
          user={user}
        />
      ) : (
        <div className="flex flex-col w-full h-full">
          <div className="bg-white w-full outline outline-1 outline-gray-200  rounded-sm h-[100px]">
            <div className="flex justify-between font-bold">
              <div className="font-bold mx-4 mt-4">
                Welcome{user && ", " + user.name} | {currentMonth}
              </div>
              <div className="flex flex-row gap-6 mx-4 mt-4">
                <div>Days Remaining: {businessDaysRemaining}</div>
                <Switch
                  enabled={gtgEnabled}
                  handleChange={() => {
                    setGtgEnabled(!gtgEnabled);
                  }}
                  label="GTG"
                />
              </div>
            </div>
            <div className="ml-4 mr-4 pb-4">
              <SectionedProgressBar
                data={progressData}
                totals={`${totalUnitsSold} / ${currentDealership?.dealershipConfiguration.unitsSoldTarget ? currentDealership?.dealershipConfiguration?.unitsSoldTarget : 0} Total`}
              />
            </div>
          </div>

          <div className="mt-8 flex flex-row gap-5 h-full">
            <Card className="w-1/2 rounded-sm outline outline-1 shadow-none outline-gray-200 min-w-0 max-h-[500px]">
              <div className="px-8 py-4 h-full">
                <div className="flex flex-row items-center justify-between border-b flex-shrink">
                  <div className="font-bold text-black">
                    {/* <select className="bg-transparent">
                      <option>Retail Sales Breakdown</option>
                      <option>All Sales Breakdown</option>
                    </select> */}
                    <div className="relative">
                      <button
                        className="flex items-center h-8 pl-3 pr-2 focus:outline-none"
                        onClick={() => {
                          setDropdownOpen(!dropdownOpen);
                        }}
                      >
                        <span className="font-bold text-black">
                          {selectedSalesOverviewOption.label}
                        </span>
                        <svg
                          className="w-4 h-4 mt-px ml-2"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {dropdownOpen && (
                        <div className="absolute flex flex-col w-48 shadow-lg bg-white">
                          <a
                            className="flex items-center h-8 px-3 text-sm hover:bg-gray-200"
                            href="#"
                            onClick={() => {
                              setSelectedSalesOverviewOption({
                                label: "All Sales Breakdown",
                                value: "ALL",
                              });
                              setDropdownOpen(false);
                            }}
                          >
                            All Sales Breakdown
                          </a>
                          <a
                            className="flex items-center h-8 px-3 text-sm hover:bg-gray-200"
                            href="#"
                            onClick={() => {
                              setSelectedSalesOverviewOption({
                                label: "Retail Sales Breakdown",
                                value: "RETAIL",
                              });
                              setDropdownOpen(false);
                            }}
                          >
                            Retail Sales Breakdown
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Checkbox
                      className="checked:bg-[#43d662]  focus:outline-none "
                      onChange={() => {
                        setSalesRange("MTD");
                      }}
                      checked={salesRange === "MTD"}
                      label={<span className="font-bold text-black">MTD</span>}
                    />
                    <Checkbox
                      className="checked:bg-[#43d662] focus:outline-none "
                      checked={salesRange === "YTD"}
                      onChange={() => {
                        setSalesRange("YTD");
                      }}
                      label={<span className="font-bold text-black">YTD</span>}
                    />
                    <Checkbox
                      className="checked:bg-[#43d662]  focus:outline-none "
                      onChange={() => {
                        setSalesRange("WTD");
                      }}
                      checked={salesRange === "WTD"}
                      label={<span className="font-bold text-black">WTD</span>}
                    />
                  </div>
                </div>
                <div className="flex justify-center h-[30vh] pb-8 w-full items-center  ">
                  <div className="flex flex-row w-full m-auto flex-shrink">
                    <div className="w-1/3 ">{totalUnitsChart}</div>
                    {userPermissions &&
                      userPermissions.includes("VIEW_OWN_GROSS") && (
                        <div className="w-1/3 ">{totalFrontGrossChart}</div>
                      )}
                    {userPermissions &&
                      (userPermissions.includes("VIEW_DASHBOARD_FULL") ||
                        userPermissions.includes("VIEW_OWN_BACK_GROSS")) && (
                        <div className="w-1/3 ">{backendGrossChart}</div>
                      )}
                  </div>
                </div>
              </div>
            </Card>

            <LeaderboardCard
              currentDealership={currentDealership}
              gtgOnly={gtgEnabled}
              primaryUserGroup={primaryUserGroup && primaryUserGroup.name}
            />
          </div>
          <div className="mt-8 flex flex-row gap-5 h-full pb-8">
            <Card className="w-1/2 shadow-none rounded-sm outline outline-1 outline-gray-200 max-h-[500px] ">
              <div className="px-8 py-4">
                <div className="flex flex-row items-center justify-between border-b">
                  <div className="font-bold text-black">
                    Overview{" "}
                    <span>
                      <TooltipComponent
                        key={"overviewTooltip"}
                        content={
                          "Overview includes ALL deals (ie. Retail, Wholesale, Fleet, etc)."
                        }
                      >
                        <i className="fal fa-info-circle fa-fw text-sm"></i>
                      </TooltipComponent>
                    </span>
                  </div>
                  <div>
                    <Checkbox
                      className="checked:bg-[#43d662]"
                      onChange={() => {
                        setOverviewRange("MTD");
                      }}
                      checked={overviewRange === "MTD"}
                      label={<span className="font-bold text-black">MTD</span>}
                    />
                    <Checkbox
                      className="checked:bg-[#43d662]"
                      checked={overviewRange === "YTD"}
                      onChange={() => {
                        setOverviewRange("YTD");
                      }}
                      label={<span className="font-bold text-black">YTD</span>}
                    />
                    <Checkbox
                      className="checked:bg-[#43d662]"
                      onChange={() => {
                        setOverviewRange("WTD");
                      }}
                      checked={overviewRange === "WTD"}
                      label={<span className="font-bold text-black">WTD</span>}
                    />
                  </div>
                </div>
                <div className="flex flex-col max-h-[30vh] h-full overflow-auto">
                  <StackedProgressBar
                    className="mt-5"
                    title="Sale Status"
                    legend={dealStatusColors}
                    options={dealStatusOptions || []}
                    width="w-[80%]"
                    tooltip={(option) => {
                      return `${option.key.toLowerCase().replace("_", " ")}: ${
                        option.amount
                      } ${
                        option.amount == 1 ? "Unit" : "Units"
                      } (${option.percentage.toFixed(2)}%)`;
                    }}
                  />

                  <StackedProgressBar
                    className="mt-5"
                    title="Sale Type"
                    legend={saleTypeColors}
                    options={saleTypeOptions}
                    width="w-[80%]"
                    tooltip={(option) => {
                      return `${
                        option.key === "DLA"
                          ? option.key
                          : option.key.toLowerCase()
                      }: ${option.amount} ${
                        option.amount == 1 ? "Unit" : "Units"
                      } (${option.percentage.toFixed(2)}%)`;
                    }}
                  />

                  <StackedProgressBar
                    className="mt-5"
                    title="Deal Type"
                    legend={dealTypeColors}
                    options={unitsByPaymentTypeOptions}
                    width="w-[80%]"
                    tooltip={(option) => {
                      return `${option.key.toLowerCase()}: ${option.amount} ${
                        option.amount == 1 ? "Unit" : "Units"
                      } (${option.percentage.toFixed(2)}%)`;
                    }}
                  />
                </div>
              </div>
            </Card>

            <DeliveryCalendarCard
              deliveries={deliveryData || {}}
              userPermissions={userPermissions || []}
              user={user!}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardPage;
